import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Text,
  Textarea,
  useDisclosure,
  VStack,
  Wrap,
  WrapItem,
} from '@chakra-ui/react'
import { X, ExternalLink, Trash2 } from 'lucide-react'
import { Link as RouterLink } from 'react-router-dom'
import type { TicketFormOption, TicketInitialData } from '@/types/ticketRequest'
import { ticketRequestToInitialData } from '@/types/ticketRequest'
import {
  backendDateTimeToLocal,
  getTicketRequest,
  loadTicketFormOptions,
  parseApiValidationErrors,
  submitTicketRequestForm,
} from '@/lib/services/ticketRequestService'
import {
  clearTicketFormDraft,
  loadTicketFormDraft,
  saveTicketFormDraft,
  type TicketFormDraft,
} from '@/lib/utils/ticketFormDraft'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import {
  TicketFormTriageSection,
  DEFAULT_TRIAGE_STATE,
  triageFromExtracted,
  type TriageFormState,
} from '@/components/client/TicketFormTriageSection'
import {
  PortalLocationSection,
  DEFAULT_PORTAL_LOCATION,
  type PortalLocationState,
} from '@/components/client/PortalLocationSection'
import { validateAvailabilityRange, parseContactPhone } from '@/lib/voiceAi/buildVoiceAiCallPayload'
import { ProfileService } from '@/lib/services/profileService'
import { useAuthStore } from '@/store/auth'

export type { TicketInitialData }

export type CreateTicketPayload = {
  service: string
  description: string
  devicesAffected: number
  preferredScheduleAt: string
  preferredContactAt: string
  contactNumber: string
  contactPerson: string
  email: string
  attachments: File[]
  existingAttachments?: Array<{ file_name: string; path: string; file_url?: string }>
  submitRequest: boolean
  storeId: string
  storeCode: string
  siteAddress: string
  errorMessage: string
  preferredScheduleUntil: string
  contactAvailabilityText: string
  workImpact: string
  othersAffected: string
  userScope: string
  appScope: string
  accessType: string
  workPermitNeeded: string
  permitAvailable: string
  permitDuration: string
  partsApproved: string
  closeOnFix: string
  regionId: string
  provinceId: string
  townId: string
  needsHumanReview?: boolean
}

type Props = {
  isOpen: boolean
  onClose: () => void
  editingId?: number | null
  duplicateFrom?: TicketInitialData | null
  defaultContact?: { name?: string; email?: string; phone?: string }
  profilePhoneMissing?: boolean
  onSuccess?: () => void
}

type FormSnapshot = {
  serviceParent: string
  serviceType: string
  description: string
  devicesAffected: number
  preferredScheduleAt: string
  preferredContactAt: string
  triageJson: string
  contactNumber: string
  contactPerson: string
  email: string
  attachmentCount: number
  existingAttachmentKeys: string
}

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf']

function buildSnapshot(state: FormSnapshot): string {
  return JSON.stringify(state)
}

export function CreateTicketModal({
  isOpen,
  onClose,
  editingId,
  duplicateFrom,
  defaultContact,
  profilePhoneMissing,
  onSuccess,
}: Props) {
  const { showSuccess, showError } = useToastNotification()
  const user = useAuthStore((s) => s.user)
  const refreshUser = useAuthStore((s) => s.refreshUser)

  const [serviceParents, setServiceParents] = useState<TicketFormOption[]>([])
  const [serviceChildren, setServiceChildren] = useState<TicketFormOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [isLoadingEdit, setIsLoadingEdit] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const [serviceParent, setServiceParent] = useState('')
  const [serviceType, setServiceType] = useState('')
  const [description, setDescription] = useState('')
  const [devicesAffected, setDevicesAffected] = useState<number>(1)
  const [preferredScheduleAt, setPreferredScheduleAt] = useState('')
  const [preferredContactAt, setPreferredContactAt] = useState('')
  const [triage, setTriage] = useState<TriageFormState>(DEFAULT_TRIAGE_STATE)
  const [location, setLocation] = useState<PortalLocationState>(DEFAULT_PORTAL_LOCATION)
  const [contactNumber, setContactNumber] = useState('')
  const [contactPerson, setContactPerson] = useState('')
  const [email, setEmail] = useState('')
  const [attachments, setAttachments] = useState<File[]>([])
  const [existingAttachments, setExistingAttachments] = useState<
    Array<{ file_name: string; path: string; file_url?: string }>
  >([])
  const [isDragging, setIsDragging] = useState(false)
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const isEditMode = editingId != null
  const isPrefillingRef = useRef(false)
  const baselineRef = useRef<string | null>(null)
  const initSessionRef = useRef<string | null>(null)
  const [baselineVersion, setBaselineVersion] = useState(0)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const profileCancelRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  const showErrorRef = useRef(showError)
  const defaultContactRef = useRef(defaultContact)

  const [pendingProfilePhone, setPendingProfilePhone] = useState<string | null>(null)
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)

  const {
    isOpen: isDiscardOpen,
    onOpen: onDiscardOpen,
    onClose: onDiscardClose,
  } = useDisclosure()

  const {
    isOpen: isProfileUpdateOpen,
    onOpen: onProfileUpdateOpen,
    onClose: onProfileUpdateClose,
  } = useDisclosure()

  const filteredServiceTypes = useMemo(() => {
    if (!serviceParent || !serviceChildren.length) return []
    return serviceChildren.filter(
      (st) => st.parent_id && String(st.parent_id) === String(serviceParent)
    )
  }, [serviceParent, serviceChildren])

  const hasChildTypes = filteredServiceTypes.length > 0
  const effectiveServiceId = hasChildTypes ? serviceType : serviceParent

  const patchTriage = (patch: Partial<TriageFormState>) => {
    setTriage((prev) => ({ ...prev, ...patch }))
  }

  const scheduleRangeError = useMemo(
    () => validateAvailabilityRange(preferredScheduleAt, triage.preferredScheduleUntil),
    [preferredScheduleAt, triage.preferredScheduleUntil]
  )

  const buildPayloadExtras = (): Omit<
    CreateTicketPayload,
    | 'service'
    | 'description'
    | 'devicesAffected'
    | 'preferredScheduleAt'
    | 'preferredContactAt'
    | 'contactNumber'
    | 'contactPerson'
    | 'email'
    | 'attachments'
    | 'existingAttachments'
    | 'submitRequest'
  > => ({
    storeId: triage.storeId,
    storeCode: triage.storeCode,
    siteAddress: triage.siteAddress,
    errorMessage: triage.errorMessage,
    preferredScheduleUntil: triage.preferredScheduleUntil,
    contactAvailabilityText: triage.contactAvailabilityText,
    workImpact: triage.workImpact,
    othersAffected: triage.othersAffected,
    userScope: triage.userScope,
    appScope: triage.appScope,
    accessType: triage.accessType,
    workPermitNeeded: triage.workPermitNeeded,
    permitAvailable: triage.permitAvailable,
    permitDuration: triage.permitDuration,
    partsApproved: triage.partsApproved,
    closeOnFix: triage.closeOnFix,
    regionId: location.regionId,
    provinceId: location.provinceId,
    townId: location.townId,
    needsHumanReview: !location.regionId && !location.provinceId && !location.townId,
  })

  const makeSnapshot = useCallback((): FormSnapshot => {
    return {
      serviceParent,
      serviceType,
      description,
      devicesAffected,
      preferredScheduleAt,
      preferredContactAt,
      triageJson: JSON.stringify(triage),
      contactNumber,
      contactPerson,
      email,
      attachmentCount: attachments.length,
      existingAttachmentKeys: existingAttachments
        .map((a) => a.path || a.file_url || a.file_name)
        .sort()
        .join('|'),
    }
  }, [
    serviceParent,
    serviceType,
    description,
    devicesAffected,
    preferredScheduleAt,
    preferredContactAt,
    triage,
    contactNumber,
    contactPerson,
    email,
    attachments.length,
    existingAttachments,
  ])

  const captureBaseline = useCallback(() => {
    baselineRef.current = buildSnapshot(makeSnapshot())
    setBaselineVersion((v) => v + 1)
  }, [makeSnapshot])

  const captureBaselineRef = useRef(captureBaseline)
  captureBaselineRef.current = captureBaseline
  onCloseRef.current = onClose
  showErrorRef.current = showError
  defaultContactRef.current = defaultContact

  const isDirty = useMemo(() => {
    if (!baselineRef.current) return false
    return buildSnapshot(makeSnapshot()) !== baselineRef.current
  }, [makeSnapshot, baselineVersion])

  const applyDefaultContact = () => {
    const contact = defaultContactRef.current
    setServiceParent('')
    setServiceType('')
    setDescription('')
    setDevicesAffected(1)
    setPreferredScheduleAt('')
    setPreferredContactAt('')
    setTriage(DEFAULT_TRIAGE_STATE)
    setContactNumber(contact?.phone ?? '')
    setContactPerson(contact?.name ?? '')
    setEmail(contact?.email ?? '')
    setAttachments([])
    setExistingAttachments([])
  }

  const applyDraft = (draft: TicketFormDraft) => {
    const contact = defaultContactRef.current
    setServiceParent(draft.serviceParent ?? '')
    setServiceType(draft.serviceType ?? '')
    setDescription(draft.description ?? '')
    setDevicesAffected(draft.devicesAffected ?? 1)
    setPreferredScheduleAt(draft.preferredScheduleAt ?? '')
    setPreferredContactAt(draft.preferredContactAt ?? '')
    setTriage({
      ...DEFAULT_TRIAGE_STATE,
      storeId: draft.storeId ?? '',
      storeCode: draft.storeCode ?? '',
      storeLabel: draft.storeLabel ?? '',
      siteAddress: draft.siteAddress ?? '',
      errorMessage: draft.errorMessage ?? '',
      preferredScheduleUntil: draft.preferredScheduleUntil ?? '',
      contactAvailabilityText: draft.contactAvailabilityText ?? '',
      workImpact: draft.workImpact ?? '',
      othersAffected: draft.othersAffected ?? '',
      userScope: draft.userScope ?? '',
      appScope: draft.appScope ?? '',
      accessType: draft.accessType ?? '',
      workPermitNeeded: draft.workPermitNeeded ?? '',
      permitAvailable: draft.permitAvailable ?? '',
      permitDuration: draft.permitDuration ?? '',
      partsApproved: draft.partsApproved ?? '',
      closeOnFix: draft.closeOnFix ?? '',
      triageExpanded: draft.triageExpanded ?? false,
    })
    setContactNumber(draft.contactNumber ?? contact?.phone ?? '')
    setContactPerson(draft.contactPerson ?? contact?.name ?? '')
    setEmail(draft.email ?? contact?.email ?? '')
    setAttachments([])
    setExistingAttachments([])
  }

  const prefillFromInitialData = (data: TicketInitialData, includeAttachments: boolean) => {
    isPrefillingRef.current = true

    const serviceParentId = String(data.service_parent_id ?? '')
    const serviceTypeId = String(data.service_type_id ?? '')
    const effectiveParentId = serviceParentId || serviceTypeId

    if (effectiveParentId) {
      setServiceParent(effectiveParentId)
    }

    setDescription(data.description ?? '')
    setDevicesAffected(data.requested_quantity ?? 1)
    setContactNumber(data.contact_number ?? '')
    setContactPerson(data.contact_name ?? '')
    setEmail(data.contact_email ?? '')
    setPreferredScheduleAt(backendDateTimeToLocal(data.preferred_schedule_at))
    setPreferredContactAt(backendDateTimeToLocal(data.preferred_contact_at))
    setTriage(
      triageFromExtracted(
        data.extracted_fields,
        backendDateTimeToLocal(data.preferred_schedule_until)
      )
    )
    setAttachments([])
    setExistingAttachments(includeAttachments ? data.attachment_metadata || [] : [])

    const finishPrefill = () => {
      isPrefillingRef.current = false
      captureBaselineRef.current()
    }

    if (serviceTypeId) {
      setTimeout(() => {
        setServiceType(serviceTypeId)
        finishPrefill()
      }, 10)
    } else {
      setServiceType('')
      finishPrefill()
    }
  }

  useEffect(() => {
    if (serviceParent && !isPrefillingRef.current) {
      setServiceType('')
    }
  }, [serviceParent])

  useEffect(() => {
    if (isOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = originalStyle
      }
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) {
      initSessionRef.current = null
      isPrefillingRef.current = false
      baselineRef.current = null
      setFieldErrors({})
      setTouched({})
      return
    }

    const sessionKey =
      editingId != null
        ? `edit:${editingId}`
        : duplicateFrom
          ? `dup:${duplicateFrom.service_type_id ?? ''}:${duplicateFrom.description ?? ''}`
          : 'create'

    if (initSessionRef.current === sessionKey) return
    initSessionRef.current = sessionKey

    let cancelled = false

    const init = async () => {
      setIsLoadingOptions(true)
      try {
        const options = await loadTicketFormOptions()
        if (cancelled) return
        setServiceParents(options.serviceParents)
        setServiceChildren(options.serviceChildren)
      } catch {
        if (!cancelled) {
          setServiceParents([])
          setServiceChildren([])
          showErrorRef.current('Failed to load form options.')
        }
      } finally {
        if (!cancelled) setIsLoadingOptions(false)
      }

      if (editingId != null) {
        setIsLoadingEdit(true)
        try {
          const data = await getTicketRequest(editingId)
          if (cancelled) return
          prefillFromInitialData(ticketRequestToInitialData(data), true)
        } catch {
          if (!cancelled) {
            showErrorRef.current('Failed to load request.')
            onCloseRef.current()
          }
        } finally {
          if (!cancelled) setIsLoadingEdit(false)
        }
      } else if (duplicateFrom) {
        const { id: _id, attachment_metadata: _meta, ...rest } = duplicateFrom
        prefillFromInitialData({ ...rest, attachment_metadata: [] }, false)
      } else {
        const draft = loadTicketFormDraft()
        if (draft) {
          isPrefillingRef.current = true
          applyDraft(draft)
          setTimeout(() => {
            captureBaselineRef.current()
            isPrefillingRef.current = false
          }, 0)
        } else {
          applyDefaultContact()
          setTimeout(() => captureBaselineRef.current(), 0)
        }
      }
    }

    void init()

    return () => {
      cancelled = true
    }
  }, [isOpen, editingId, duplicateFrom])

  useEffect(() => {
    if (!isOpen || isEditMode || duplicateFrom) return

    const timer = setTimeout(() => {
      saveTicketFormDraft({
        serviceParent,
        serviceType,
        description,
        devicesAffected,
        preferredScheduleAt,
        preferredScheduleUntil: triage.preferredScheduleUntil,
        preferredContactAt,
        contactNumber,
        contactPerson,
        email,
        storeId: triage.storeId,
        storeCode: triage.storeCode,
        storeLabel: triage.storeLabel,
        siteAddress: triage.siteAddress,
        errorMessage: triage.errorMessage,
        contactAvailabilityText: triage.contactAvailabilityText,
        workImpact: triage.workImpact,
        othersAffected: triage.othersAffected,
        userScope: triage.userScope,
        appScope: triage.appScope,
        accessType: triage.accessType,
        workPermitNeeded: triage.workPermitNeeded,
        permitAvailable: triage.permitAvailable,
        permitDuration: triage.permitDuration,
        partsApproved: triage.partsApproved,
        closeOnFix: triage.closeOnFix,
        triageExpanded: triage.triageExpanded,
      })
    }, 500)

    return () => clearTimeout(timer)
  }, [
    isOpen,
    isEditMode,
    duplicateFrom,
    serviceParent,
    serviceType,
    description,
    devicesAffected,
    preferredScheduleAt,
    preferredContactAt,
    contactNumber,
    contactPerson,
    email,
    triage,
  ])

  const previewUrls = useMemo(() => {
    return attachments
      .filter((file) => file.type.startsWith('image/'))
      .map((file) => URL.createObjectURL(file))
  }, [attachments])

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [previewUrls])

  const contactPhoneHint = useMemo(() => {
    if (!contactNumber.trim()) return null
    if (!parseContactPhone(contactNumber)) {
      return 'Enter a valid Philippine mobile number (e.g. 09171234567 or 9171234567).'
    }
    return null
  }, [contactNumber])

  const errors = useMemo(() => {
    const e: Record<string, string> = {}

    if (!serviceParent) e.serviceParent = 'Service is required'
    if (hasChildTypes && !serviceType) e.serviceType = 'Type of Service is required'
    if (!description.trim()) e.description = 'Issue Description is required'

    if (!contactNumber.trim()) e.contactNumber = 'Contact Number is required'
    if (!contactPerson.trim()) e.contactPerson = 'Contact Person is required'

    if (!email.trim()) e.email = 'Email Address is required'
    else if (!/^\S+@\S+\.\S+$/.test(email)) e.email = 'Enter a valid email address'

    if (!devicesAffected || devicesAffected < 1) e.devicesAffected = 'Must be at least 1'

    const tooLarge = attachments.find((f) => f.size > 5 * 1024 * 1024)
    if (tooLarge) e.attachments = 'Each file must be 5MB or less'

    if (scheduleRangeError) e.preferredScheduleUntil = scheduleRangeError

    return { ...e, ...fieldErrors }
  }, [
    serviceParent,
    serviceType,
    hasChildTypes,
    description,
    contactNumber,
    contactPerson,
    email,
    devicesAffected,
    attachments,
    fieldErrors,
    scheduleRangeError,
  ])

  const hasErrors = Object.keys(errors).length > 0

  function markTouched(key: string) {
    setTouched((prev) => ({ ...prev, [key]: true }))
  }

  function handleResetFromProfile() {
    if (!defaultContact) return
    setContactNumber(defaultContact.phone ?? '')
    setContactPerson(defaultContact.name ?? '')
    setEmail(defaultContact.email ?? '')
  }

  function requestClose() {
    if (isDirty) {
      onDiscardOpen()
    } else {
      onClose()
    }
  }

  function confirmDiscard() {
    onDiscardClose()
    onClose()
  }

  async function handleSubmit(submitRequest: boolean) {
    setTouched({
      serviceParent: true,
      serviceType: true,
      description: true,
      preferredScheduleAt: true,
      preferredContactAt: true,
      contactNumber: true,
      contactPerson: true,
      email: true,
      devicesAffected: true,
      attachments: true,
    })

    if (hasErrors) return

    const submittedContactNumber = contactNumber.trim()

    setIsSubmitting(true)
    setFieldErrors({})

    try {
      await submitTicketRequestForm(
        {
          service: effectiveServiceId,
          description: description.trim(),
          devicesAffected,
          preferredScheduleAt,
          preferredContactAt,
          contactNumber: submittedContactNumber,
          contactPerson: contactPerson.trim(),
          email: email.trim(),
          attachments,
          existingAttachments: existingAttachments.length > 0 ? existingAttachments : undefined,
          submitRequest,
          ...buildPayloadExtras(),
        },
        editingId
      )

      showSuccess(
        submitRequest
          ? editingId != null
            ? 'Request submitted'
            : 'Ticket created'
          : 'Draft saved',
        submitRequest
          ? 'Your request has been submitted.'
          : editingId != null
            ? 'Your draft has been saved.'
            : 'Your draft has been saved.'
      )

      clearTicketFormDraft()
      onSuccess?.()

      const shouldPromptProfile =
        defaultContact != null &&
        submittedContactNumber !== (defaultContact.phone ?? '').trim()

      baselineRef.current = null
      onClose()

      if (shouldPromptProfile) {
        setPendingProfilePhone(submittedContactNumber)
        onProfileUpdateOpen()
      }
    } catch (err) {
      const apiErrors = parseApiValidationErrors(err)
      if (Object.keys(apiErrors).length > 0) {
        setFieldErrors(apiErrors)
      }
      showError(err)
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleProfileUpdateConfirm() {
    if (!user?.id || !pendingProfilePhone) {
      onProfileUpdateClose()
      setPendingProfilePhone(null)
      return
    }

    setIsUpdatingProfile(true)
    try {
      const u = user as unknown as Record<string, unknown>
      await ProfileService.updateProfile({
        id: user.id,
        user_login: user.user_login,
        user_email: (u.user_email as string) ?? user.email,
        contact_number: pendingProfilePhone,
        first_name: (u.first_name as string) ?? '',
        last_name: (u.last_name as string) ?? '',
        nickname: (u.nickname as string) ?? '',
        biography: (u.biography as string) ?? '',
        attachment_file: (u.attachment_file as string) ?? '',
      })
      await refreshUser()
      showSuccess('Profile updated', 'Your contact number has been saved to your profile.')
    } catch (err) {
      showError(err)
    } finally {
      setIsUpdatingProfile(false)
      onProfileUpdateClose()
      setPendingProfilePhone(null)
    }
  }

  function filterAllowedFiles(files: FileList | File[]): File[] {
    const list = Array.isArray(files) ? files : Array.from(files)
    return list.filter((f) => ALLOWED_FILE_TYPES.includes(f.type))
  }

  function onFilesSelected(files: FileList | null) {
    if (!files) return
    const filtered = filterAllowedFiles(files)
    setAttachments((prev) => [...prev, ...filtered])
    markTouched('attachments')
  }

  function onDragOver(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  function onDragLeave(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer?.files
    if (!files?.length) return
    const filtered = filterAllowedFiles(files)
    setAttachments((prev) => [...prev, ...filtered])
    markTouched('attachments')
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={requestClose}
        size="2xl"
        isCentered
        blockScrollOnMount
        scrollBehavior="inside"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />

        <ModalContent
          borderRadius="md"
          maxH="90vh"
          position="relative"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader py={3} flexShrink={0}>
            <HStack justify="space-between" align="center">
              <Text fontSize="md" fontWeight="700" color="red.700">
                {isEditMode ? 'Edit Ticket' : 'Create New Ticket'}
              </Text>

              <IconButton
                aria-label="Close"
                icon={<X size={18} />}
                variant="ghost"
                onClick={requestClose}
              />
            </HStack>
          </ModalHeader>

          <ModalCloseButton display="none" />
          <Divider />

          <ModalBody
            pt={4}
            pb={1}
            px={6}
            overflowY="auto"
            overflowX="hidden"
            flex="1 1 auto"
            minH={0}
            maxH="calc(90vh - 100px)"
            position="relative"
            sx={{
              '& > *': {
                position: 'relative',
              },
              '&::-webkit-scrollbar': { width: '8px' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.18)',
                borderRadius: '999px',
                border: '2px solid transparent',
                backgroundClip: 'content-box',
              },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
            }}
          >
            {isLoadingEdit ? (
              <Box py={8} textAlign="center" color="gray.500">
                Loading request...
              </Box>
            ) : (
              <VStack align="stretch" spacing={3} flex="0 0 auto" pb={0} mb={0}>
                {profilePhoneMissing && (
                  <Alert status="warning" borderRadius="md">
                    <AlertDescription fontSize="sm">
                      Your profile is missing a contact number.{' '}
                      <Link as={RouterLink} to="/profile" color="blue.600" fontWeight="600">
                        Update your profile
                      </Link>
                    </AlertDescription>
                  </Alert>
                )}

                <Box>
                  <Text fontWeight="700" fontSize="sm" mb={2} color="blue.900">
                    Request Details
                  </Text>

                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3} rowGap={3}>
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl
                        isInvalid={!!errors.serviceParent && touched.serviceParent}
                        isRequired
                      >
                        <FormLabel fontSize="sm">Service</FormLabel>
                        <Select
                          value={serviceParent}
                          onChange={(e) => setServiceParent(e.target.value)}
                          onBlur={() => markTouched('serviceParent')}
                          placeholder="Select service"
                          isDisabled={isLoadingOptions}
                        >
                          {serviceParents.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Select>
                        <FormErrorMessage>{errors.serviceParent}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    {hasChildTypes && (
                      <GridItem colSpan={{ base: 1, md: 2 }}>
                        <FormControl
                          isInvalid={!!errors.serviceType && touched.serviceType}
                          isRequired
                        >
                          <FormLabel fontSize="sm">Type of Service</FormLabel>
                          <Select
                            value={serviceType}
                            onChange={(e) => setServiceType(e.target.value)}
                            onBlur={() => markTouched('serviceType')}
                            placeholder="Select type"
                            isDisabled={!serviceParent || isLoadingOptions}
                          >
                            {filteredServiceTypes.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Select>
                          <FormErrorMessage>{errors.serviceType}</FormErrorMessage>
                        </FormControl>
                      </GridItem>
                    )}

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl
                        isInvalid={!!errors.description && touched.description}
                        isRequired
                      >
                        <FormLabel fontSize="sm">Issue Description</FormLabel>
                        <Textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          onBlur={() => markTouched('description')}
                          placeholder="e.g., Dirty printout with line / Paper jam on Tray 1 / Printer not connecting to network"
                          minH="100px"
                          resize="none"
                        />
                        <FormErrorMessage>{errors.description}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl
                        isInvalid={!!errors.devicesAffected && touched.devicesAffected}
                      >
                        <FormLabel fontSize="sm">Count of devices affected</FormLabel>
                        <NumberInput
                          value={devicesAffected}
                          min={1}
                          onChange={(_, n) => setDevicesAffected(Number.isNaN(n) ? 1 : n)}
                          onBlur={() => markTouched('devicesAffected')}
                        >
                          <NumberInputField />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                        <FormErrorMessage>{errors.devicesAffected}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl>
                        <FormLabel fontSize="sm">Dispatch from</FormLabel>
                        <Input
                          type="datetime-local"
                          size="md"
                          value={preferredScheduleAt}
                          onChange={(e) => {
                            setPreferredScheduleAt(e.target.value)
                            markTouched('preferredScheduleAt')
                          }}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <PortalLocationSection
                        value={location}
                        onChange={(patch) => setLocation((prev) => ({ ...prev, ...patch }))}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <TicketFormTriageSection
                        serviceParents={serviceParents}
                        serviceParent={serviceParent}
                        preferredScheduleAt={preferredScheduleAt}
                        triage={triage}
                        onChange={patchTriage}
                        scheduleRangeError={scheduleRangeError}
                      />
                    </GridItem>

                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <FormControl isInvalid={!!errors.attachments && touched.attachments}>
                        <FormLabel fontSize="sm">Attach files (images or PDF)</FormLabel>

                        <Box
                          border="2px dashed"
                          borderColor={isDragging ? 'blue.400' : 'gray.300'}
                          borderRadius="md"
                          p={4}
                          textAlign="center"
                          bg={isDragging ? 'blue.50' : 'transparent'}
                          transition="background 0.2s, border-color 0.2s"
                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={onDrop}
                        >
                          <VStack spacing={2}>
                            <Text fontSize="sm" color="gray.500">
                              Drag and drop files here, or click to browse
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              Supported formats: JPG, PNG, GIF, PDF (max 5 MB each)
                            </Text>

                            <Input
                              type="file"
                              accept="image/jpeg,image/png,image/gif,application/pdf"
                              multiple
                              onChange={(e) => onFilesSelected(e.target.files)}
                              display="none"
                              id="client-ticket-attachments"
                            />

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                document.getElementById('client-ticket-attachments')?.click()
                              }
                            >
                              Browse files
                            </Button>

                            {attachments.length > 0 && (
                              <Box pt={3} w="100%">
                                <Text fontSize="xs" fontWeight="600" color="gray.700" mb={2}>
                                  New Files ({attachments.length}):
                                </Text>
                                <Wrap spacing={2}>
                                  {attachments.map((file, index) => {
                                    const isImage = file.type.startsWith('image/')
                                    const imageFiles = attachments.filter((f) =>
                                      f.type.startsWith('image/')
                                    )
                                    const imageIndex = imageFiles.findIndex((f) => f === file)
                                    const previewUrl =
                                      isImage && imageIndex >= 0 ? previewUrls[imageIndex] : null

                                    return (
                                      <WrapItem key={index}>
                                        <Box
                                          position="relative"
                                          w="80px"
                                          h="80px"
                                          borderRadius="md"
                                          overflow="hidden"
                                          border="1px solid"
                                          borderColor="gray.300"
                                          bg="gray.100"
                                        >
                                          {isImage && previewUrl ? (
                                            <>
                                              <Box
                                                as="img"
                                                src={previewUrl}
                                                alt={file.name}
                                                w="100%"
                                                h="100%"
                                                objectFit="cover"
                                              />
                                              <IconButton
                                                aria-label="Remove file"
                                                icon={<X size={14} />}
                                                size="xs"
                                                position="absolute"
                                                top={1}
                                                right={1}
                                                bg="red.500"
                                                color="white"
                                                _hover={{ bg: 'red.600' }}
                                                onClick={() => {
                                                  setAttachments((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                  )
                                                }}
                                              />
                                            </>
                                          ) : (
                                            <VStack
                                              h="100%"
                                              justify="center"
                                              spacing={0}
                                              p={1}
                                              bg="blue.50"
                                              borderColor="blue.200"
                                            >
                                              <Text
                                                fontSize="xs"
                                                color="blue.600"
                                                fontWeight="600"
                                                textAlign="center"
                                              >
                                                {file.type === 'application/pdf' ? 'PDF' : 'FILE'}
                                              </Text>
                                              <IconButton
                                                aria-label="Remove file"
                                                icon={<X size={12} />}
                                                size="xs"
                                                position="absolute"
                                                top={1}
                                                right={1}
                                                bg="red.500"
                                                color="white"
                                                _hover={{ bg: 'red.600' }}
                                                onClick={() => {
                                                  setAttachments((prev) =>
                                                    prev.filter((_, i) => i !== index)
                                                  )
                                                }}
                                              />
                                            </VStack>
                                          )}
                                        </Box>
                                      </WrapItem>
                                    )
                                  })}
                                </Wrap>
                              </Box>
                            )}

                            {existingAttachments.length > 0 && attachments.length === 0 && (
                              <Box pt={2} w="100%">
                                <Text fontSize="xs" color="gray.600">
                                  {existingAttachments.length} existing file(s)
                                </Text>
                              </Box>
                            )}
                          </VStack>
                        </Box>

                        {existingAttachments.length > 0 && (
                          <Box mt={3}>
                            <Text fontSize="xs" fontWeight="600" color="gray.700" mb={2}>
                              Existing Attachments:
                            </Text>
                            <VStack align="stretch" spacing={2}>
                              {existingAttachments.map((attachment, index) => {
                                const fileUrl = attachment.file_url || attachment.path
                                const isImage = attachment.file_name?.match(
                                  /\.(jpg|jpeg|png|gif|webp)$/i
                                )

                                return (
                                  <HStack
                                    key={index}
                                    p={2}
                                    bg="gray.50"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor="gray.200"
                                    spacing={3}
                                    align="center"
                                  >
                                    {isImage && fileUrl ? (
                                      <Box
                                        w="60px"
                                        h="60px"
                                        borderRadius="md"
                                        overflow="hidden"
                                        flexShrink={0}
                                        border="1px solid"
                                        borderColor="gray.300"
                                        bg="gray.100"
                                        cursor="pointer"
                                        onClick={() => fileUrl && window.open(fileUrl, '_blank')}
                                        _hover={{ opacity: 0.8 }}
                                      >
                                        <Box
                                          as="img"
                                          src={fileUrl}
                                          alt={attachment.file_name}
                                          w="100%"
                                          h="100%"
                                          objectFit="cover"
                                        />
                                      </Box>
                                    ) : (
                                      <Box
                                        w="60px"
                                        h="60px"
                                        borderRadius="md"
                                        bg="blue.50"
                                        border="1px solid"
                                        borderColor="blue.200"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        flexShrink={0}
                                      >
                                        <Text fontSize="xs" color="blue.600" fontWeight="600">
                                          {attachment.file_name?.match(/\.pdf$/i) ? 'PDF' : 'FILE'}
                                        </Text>
                                      </Box>
                                    )}

                                    <Box flex="1" minW={0}>
                                      <Text
                                        fontSize="sm"
                                        color="gray.700"
                                        fontWeight="500"
                                        isTruncated
                                      >
                                        {attachment.file_name}
                                      </Text>
                                      {isImage && (
                                        <Text fontSize="xs" color="gray.500">
                                          Image
                                        </Text>
                                      )}
                                    </Box>

                                    <HStack spacing={1}>
                                      {fileUrl && (
                                        <IconButton
                                          aria-label="View file"
                                          icon={<ExternalLink size={16} />}
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => window.open(fileUrl, '_blank')}
                                        />
                                      )}
                                      <IconButton
                                        aria-label="Remove file"
                                        icon={<Trash2 size={16} />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => {
                                          setExistingAttachments((prev) =>
                                            prev.filter((_, i) => i !== index)
                                          )
                                        }}
                                      />
                                    </HStack>
                                  </HStack>
                                )
                              })}
                            </VStack>
                          </Box>
                        )}

                        <FormErrorMessage>{errors.attachments}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>

                <Divider my={2} />

                <Box>
                  <HStack justify="space-between" align="center" mb={2}>
                    <Text fontWeight="700" fontSize="sm" color="blue.900">
                      Contact Details
                    </Text>
                    {defaultContact && (
                      <Button
                        variant="link"
                        size="sm"
                        colorScheme="blue"
                        onClick={handleResetFromProfile}
                      >
                        Reset from Profile
                      </Button>
                    )}
                  </HStack>

                  <Grid templateColumns="repeat(2, 1fr)" gap={3} rowGap={3}>
                    <GridItem>
                      <FormControl
                        isInvalid={!!errors.contactNumber && touched.contactNumber}
                        isRequired
                      >
                        <FormLabel fontSize="sm">Contact Number</FormLabel>
                        <Input
                          value={contactNumber}
                          onChange={(e) => setContactNumber(e.target.value)}
                          onBlur={() => markTouched('contactNumber')}
                          placeholder="e.g., 09xxxxxxxxx"
                        />
                        {contactPhoneHint && !errors.contactNumber && (
                          <FormHelperText color="orange.600">{contactPhoneHint}</FormHelperText>
                        )}
                        <FormErrorMessage>{errors.contactNumber}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl
                        isInvalid={!!errors.contactPerson && touched.contactPerson}
                        isRequired
                      >
                        <FormLabel fontSize="sm">Contact Person</FormLabel>
                        <Input
                          value={contactPerson}
                          onChange={(e) => setContactPerson(e.target.value)}
                          onBlur={() => markTouched('contactPerson')}
                          placeholder="e.g., Juan Dela Cruz"
                        />
                        <FormErrorMessage>{errors.contactPerson}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.email && touched.email} isRequired>
                        <FormLabel fontSize="sm">Email Address</FormLabel>
                        <Input
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          onBlur={() => markTouched('email')}
                          placeholder="e.g., name@company.com"
                        />
                        <FormErrorMessage>{errors.email}</FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>
                </Box>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter
            flexShrink={0}
            borderTop="1px solid"
            borderColor="gray.200"
            bg="white"
            py={3}
          >
            <HStack justify="flex-end" spacing={3} w="100%">
              <Button variant="ghost" onClick={requestClose}>
                Cancel
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSubmit(false)}
                isLoading={isSubmitting}
                loadingText="Saving..."
                isDisabled={hasErrors}
              >
                Save as Draft
              </Button>
              <Button
                bg="red.700"
                _hover={{ bg: 'red.800' }}
                color="white"
                onClick={() => handleSubmit(true)}
                isLoading={isSubmitting}
                loadingText={isEditMode ? 'Submitting...' : 'Creating...'}
                isDisabled={hasErrors}
              >
                {isEditMode ? 'Submit Request' : 'Create Ticket'}
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertDialog
        isOpen={isDiscardOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDiscardClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Discard unsaved changes?
            </AlertDialogHeader>
            <AlertDialogBody>
              You have unsaved changes. Are you sure you want to close without saving?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDiscardClose}>
                Keep editing
              </Button>
              <Button colorScheme="red" onClick={confirmDiscard} ml={3}>
                Discard
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog
        isOpen={isProfileUpdateOpen}
        leastDestructiveRef={profileCancelRef}
        onClose={() => {
          onProfileUpdateClose()
          setPendingProfilePhone(null)
        }}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Update your profile?
            </AlertDialogHeader>
            <AlertDialogBody>
              Update your Profile with this contact number ({pendingProfilePhone})?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={profileCancelRef}
                onClick={() => {
                  onProfileUpdateClose()
                  setPendingProfilePhone(null)
                }}
              >
                Not now
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleProfileUpdateConfirm}
                ml={3}
                isLoading={isUpdatingProfile}
              >
                Update Profile
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}

export default CreateTicketModal
